/**
 * Forgot Password Modal - Request password reset
 */
import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
} from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import api from '../../services/api';
import { validateEmail } from '../../utils/validation';

interface ForgotPasswordModalProps {
  open: boolean;
  onClose: () => void;
}

interface ForgotPasswordData {
  email: string;
}

const ForgotPasswordModal: React.FC<ForgotPasswordModalProps> = ({ open, onClose }) => {
  const [email, setEmail] = useState('');
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const forgotPasswordMutation = useMutation({
    mutationFn: async (data: ForgotPasswordData) => {
      const response = await api.post('/auth/forgot-password', data);
      return response.data;
    },
    onSuccess: () => {
      setSuccess(true);
      setError('');
    },
    onError: (err: any) => {
      const detail = err.response?.data?.detail;
      const message = Array.isArray(detail) ? detail[0]?.msg ?? 'Invalid input' : detail;
      setError(message || 'Failed to send reset email');
      setSuccess(false);
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    const trimmedEmail = email.trim();
    if (!trimmedEmail) {
      setError('Please enter your email address');
      return;
    }

    const emailFormatError = validateEmail(trimmedEmail);
    if (emailFormatError) {
      setError(emailFormatError);
      return;
    }

    forgotPasswordMutation.mutate({ email: trimmedEmail });
  };

  const handleClose = () => {
    setEmail('');
    setSuccess(false);
    setError('');
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '12px',
          p: 1,
        },
      }}
    >
      <form onSubmit={handleSubmit}>
        <DialogTitle sx={{ fontSize: '1.5rem', fontWeight: 600, pb: 1 }}>
          Forgot Password
        </DialogTitle>

        <DialogContent>
          {success ? (
            <Box sx={{ py: 2 }}>
              <Alert severity="success" sx={{ mb: 2 }}>
                Temporary Password have been sent to your email address.
              </Alert>
              <Typography variant="body2" color="text.secondary">
                Please check your email and login with the temporary password.
              </Typography>
            </Box>
          ) : (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Enter your email address and we'll send you Temporary password.
              </Typography>

              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}

              <TextField
                fullWidth
                label="Email Address"
                type="email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  if (error) setError('');
                }}
                autoFocus
                required
                disabled={forgotPasswordMutation.isPending}
                error={!!error}
                helperText={error || 'Enter an email address registered in the system'}
                sx={{ mb: 2 }}
              />
            </>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={handleClose}
            variant="outlined"
            disabled={forgotPasswordMutation.isPending}
          >
            {success ? 'Close' : 'Cancel'}
          </Button>
          {!success && (
            <Button
              type="submit"
              variant="contained"
              disabled={forgotPasswordMutation.isPending}
              startIcon={forgotPasswordMutation.isPending && <CircularProgress size={20} />}
            >
              {forgotPasswordMutation.isPending ? 'Sending...' : 'Send Reset Link'}
            </Button>
          )}
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default ForgotPasswordModal;
